import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { previewFolio, createFrap, updateFrap, getFrap } from '@/utils/api';
import { generateFrapPdf } from '@/utils/generatePdf';
import SignatureCanvas from '@/components/SignatureCanvas';
import BodyDiagram from '@/components/BodyDiagram';
import { ArrowLeft, FileDown, AlertCircle, Loader2, ChevronRight, Save, CheckCircle } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_9c5cf9e4-7ffa-44c2-89bf-7aacf8535caa/artifacts/4q3hto4h_634037357_1389521753189000_3144761508300406294_n.jpg';

function SH({ id, title }) {
  return (
    <div id={`sec-${id}`} className="bg-[#1a7a4a] text-white font-bold uppercase text-xs px-4 py-2.5 tracking-widest flex items-center gap-2 shadow-sm">
      <span className="w-1.5 h-4 bg-white/40 rounded-full" />
      {title}
    </div>
  );
}

function F({ label, name, value, onChange, type = 'text', className = '', required = false, placeholder = '', min, max, testId }) {
  return (
    <div className={`flex flex-col gap-0.5 ${className}`}>
      <label className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest leading-tight">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(name, e.target.value)}
        required={required}
        placeholder={placeholder}
        min={min}
        max={max}
        className="border-0 border-b border-slate-300 bg-transparent focus:border-[#1a7a4a] focus:outline-none text-sm py-0.5 px-0 font-medium text-slate-900 placeholder:text-slate-300 transition-colors"
        data-testid={testId || `field-${name}`}
      />
    </div>
  );
}

function TA({ label, name, value, onChange, rows = 3 }) {
  return (
    <div className="flex flex-col gap-0.5">
      <label className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest">{label}</label>
      <textarea
        value={value || ''}
        onChange={e => onChange(name, e.target.value)}
        rows={rows}
        className="border border-slate-300 bg-transparent focus:border-[#1a7a4a] focus:outline-none text-sm p-1.5 font-medium text-slate-900 resize-none transition-colors rounded-sm"
        data-testid={`field-${name}`}
      />
    </div>
  );
}

function CB({ label, name, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-1.5 cursor-pointer select-none group min-w-fit">
      <div
        onClick={() => onChange(name, !checked)}
        className={`w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 cursor-pointer transition-all ${checked ? 'bg-[#1a7a4a] border-[#1a7a4a]' : 'border-slate-400 bg-white group-hover:border-[#1a7a4a]'}`}
        data-testid={testId || `cb-${name}`}
      >
        {checked && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
      </div>
      <span className={`text-xs uppercase font-medium tracking-wide ${checked ? 'text-[#1a7a4a] font-bold' : 'text-slate-700'}`}>{label}</span>
    </label>
  );
}

function RadioBtn({ label, name, value, current, onChange, color }) {
  const sel = current === value;
  const colors = { ROJO: '#dc2626', VERDE: '#1a7a4a', AMARILLO: '#d97706', NEGRO: '#1e1e1e' };
  const bg = colors[value] || colors[color];
  return (
    <button
      type="button"
      onClick={() => onChange(name, value)}
      className={`px-3 py-1.5 text-xs font-black uppercase tracking-wider border-2 transition-all rounded-sm min-w-[70px] text-center active:scale-95`}
      style={{ borderColor: bg, backgroundColor: sel ? bg : 'transparent', color: sel ? '#fff' : bg }}
      data-testid={`radio-${name}-${value}`}
    >
      {label}
    </button>
  );
}

const SECTIONS = [
  { id: 'incidente', label: 'Incidente' },
  { id: 'paciente', label: 'Paciente' },
  { id: 'origen', label: 'Origen Probable' },
  { id: 'accidente', label: 'Accidente' },
  { id: 'zona-lesion', label: 'Zona de Lesión' },
  { id: 'agente', label: 'Agente Causal' },
  { id: 'condicion', label: 'Condición / Prioridad' },
  { id: 'via-aerea', label: 'Vía Aérea' },
  { id: 'ventilacion', label: 'Ventilación' },
  { id: 'hemorragias', label: 'Hemorragias' },
  { id: 'interrogatorio', label: 'Interrogatorio' },
  { id: 'signos-vitales', label: 'Signos Vitales' },
  { id: 'escalas', label: 'Escalas' },
  { id: 'maternal', label: 'Maternal / RN' },
  { id: 'firmas', label: 'Firmas' },
];

const getLocalDate = () => {
  const hoy = new Date();
  const anio = hoy.getFullYear();
  const mes = String(hoy.getMonth() + 1).padStart(2, '0');
  const dia = String(hoy.getDate()).padStart(2, '0');
  return `${anio}-${mes}-${dia}`;
};

const initForm = () => ({
  folio: '',
  fecha: getLocalDate(),
  hora_llamada: '', hora_salida: '', hora_llegada: '',
  hora_traslado: '', hora_hospital: '', hora_base: '',

  motivo_atencion: '',
  incidente_calle: '', incidente_colonia: '', incidente_delegacion: '',
  lugar_ocurrencia_vp: false, lugar_ocurrencia_hogar: false, lugar_ocurrencia_trabajo: false,
  lugar_ocurrencia_escuela: false, lugar_ocurrencia_recreativo: false, lugar_ocurrencia_otro: false, lugar_ocurrencia_otro_texto: '',
  operador: '', prestadores_servicio: '',

  nombre_paciente: '', nombre_acompanante: '',
  sexo: '',
  edad_anios: '', edad_meses: '',
  domicilio: '', colonia: '', delegacion: '',
  telefono: '', ocupacion: '',
  derechohabiente: '', compania_seguros: '',

  origen_neurologia: false, origen_digestiva: false, origen_intoxicacion: false,
  origen_cardiovascular: false, origen_urogenital: false, origen_infecciosa: false,
  origen_respiratorio: false, origen_gineco: false, origen_oncologico: false,
  origen_metabolico: false, origen_cognitivo: false, origen_otro: false,
  origen_especifique: '',
  primera_vez: false, subsecuente: false,

  acc_colision: false, acc_volcadura: false,
  acc_col_automotor: false, acc_col_moto: false, acc_col_bici: false, acc_col_maquinaria: false,
  acc_vol_automotor: false, acc_vol_moto: false, acc_vol_bici: false, acc_vol_maquinaria: false,
  contra_fijo: false, contra_movimiento: false,
  impacto_frontal: false, impacto_lateral: false, impacto_posterior: false,
  hundimiento_cms: '',
  parabrisas_roto: false, parabrisas_doblado: false,
  volante_integro: false, volante_doblado: false,
  bolsa_si: false, bolsa_no: false,
  cinturon_colocado: false, cinturon_no: false,
  dentro_si: false, dentro_no: false, eyectado: false,
  atrop_automotor: false, atrop_moto: false, atrop_bici: false, atrop_maquinaria: false,
  casco_si: false, casco_no: false,

  agente_arma: false, agente_maquinaria: false, agente_electricidad: false,
  agente_juguete: false, agente_herramienta: false, agente_explosion: false,
  agente_automotor: false, agente_fuego: false, agente_ser_humano: false,
  agente_bicicleta: false, agente_sust_caliente: false, agente_animal: false,
  agente_prod_biologico: false, agente_sust_toxica: false, agente_otro: false,
  agente_especifique: '', lesiones_causadas_por: '',

  cond_critico_inestable: false, cond_critico_estable: false, cond_no_critico: false,
  prioridad: '',

  via_aspiracion: false, via_orofaringea: false, via_nasofaringea: false,
  cervical_manual: false, cervical_rigido: false, cervical_blando: false,

  vent_balon: false, vent_auto: false,
  vent_frec: '', vent_vol: '',
  vent_puntas: false, vent_mascarilla_simple: false, vent_mascarilla_res: false,
  vent_lts_min: '',

  hem_presion_directa: false, hem_torniquete: false, hem_vendaje: false,
  hem_via_iv: '', hem_cateter: '',
  hem_sitio_mano: false, hem_sitio_pliegue: false,
  sol_hartmann: false, sol_nacl: false, sol_mixta: false, sol_glucosa: false, sol_otra: false,

  alergias: '', medicamentos: '', enfermedades_previas: '',
  hora_ultima_comida: '', eventos_previos: '',

  // Signos Vitales
  sv_rows: [{ hora: '', fc: '', fr: '', tas: '', tad: '', temp: '', spo2: '', glucosa: '', ekg: '', pupilas: '', piel: '', llenado_cap: '', dolor: '' }],

  observaciones: '',
  // Glasgow detallado
  glasgow_ocular: '', glasgow_verbal: '', glasgow_motor: '', glasgow_total: '',
  cincinnati_asimetria_facial: '',
  cincinnati_paresia_brazos: '',
  cincinnati_alteracion_lenguaje: '',
  trauma_gcs: '', trauma_pas: '', trauma_fr: '', trauma_esf: '', trauma_llen: '',
  trauma_total: '',

  destino_trasladado: false, destino_no_trasladado: false, destino_fuga: false,

  lesion_zonas: [],

  gesta: '', cesareas: '', para: '', abortos: '', fum: '',
  semanas_gestacion: '', fecha_probable_parto: '',
  hora_contracciones: '', frec_contracciones: '', dur_contracciones: '',
  hora_nacimiento: '', lugar_nacimiento: '',
  placenta_expulsada: false,
  prod_vivo: false, prod_muerto: false,
  recien_sexo: '',
  apgar_apariencia: '', apgar_pulso: '', apgar_gesticulacion: '', apgar_actividad: '', apgar_respiracion: '', apgar_total: '',
  prod_texto: '',

  firma_pac_negativa: null, nombre_pac_negativa: '',
  firma_testigo: null, nombre_testigo: '',
  firma_pac_consentimiento: null, nombre_pac_consentimiento: '',
  firma_familiar: null, nombre_familiar: '',
  firma_entrega: null, nombre_entrega: '',
  firma_medico: null, nombre_medico: '',
  autoridades_extra: '',
  creado_por: '',
});

export default function FrapForm({ readOnly: readOnlyProp }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  // Unit users can only view saved FRAPs, not edit
  const readOnly = readOnlyProp || (isEdit && user?.role !== 'admin');

  const [form, setForm] = useState(() => ({ ...initForm(), creado_por: '' }));
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [activeSection, setActiveSection] = useState('incidente');
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    if (isEdit) {
      // Load existing FRAP for editing
      getFrap(editId)
        .then(data => {
          const fd = data.form_data || data;
          setForm({ ...initForm(), ...fd, creado_por: fd.creado_por || user?.name || '' });
        })
        .catch(() => setSaveError('No se pudo cargar el FRAP para editar'));
    } else {
      // Preview folio without incrementing
      previewFolio()
        .then(f => setForm(p => ({ ...p, folio: `~${f}`, creado_por: user?.name || '' })))
        .catch(() => { });
    }
  }, [editId, isEdit, user]);

  // Auto-calculate glasgow total
  useEffect(() => {
    const o = parseInt(form.glasgow_ocular) || 0;
    const v = parseInt(form.glasgow_verbal) || 0;
    const m = parseInt(form.glasgow_motor) || 0;
    if (form.glasgow_ocular || form.glasgow_verbal || form.glasgow_motor) {
      setForm(p => ({ ...p, glasgow_total: String(o + v + m) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.glasgow_ocular, form.glasgow_verbal, form.glasgow_motor]);

  // Auto-calculate APGAR total
  useEffect(() => {
    const scores = ['apgar_apariencia', 'apgar_pulso', 'apgar_gesticulacion', 'apgar_actividad', 'apgar_respiracion'];
    const total = scores.reduce((sum, k) => sum + (form[k] !== '' ? parseInt(form[k]) : 0), 0);
    setForm(p => {
      const hasAny = scores.some(k => p[k] !== '' && p[k] !== undefined);
      return { ...p, apgar_total: hasAny ? String(total) : '' };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.apgar_apariencia, form.apgar_pulso, form.apgar_gesticulacion, form.apgar_actividad, form.apgar_respiracion]);

  // Auto-calculate trauma total whenever any score changes
  useEffect(() => {
    const scores = ['trauma_gcs', 'trauma_pas', 'trauma_fr', 'trauma_esf', 'trauma_llen'];
    const total = scores.reduce((sum, k) => sum + (parseInt(form[k]) || 0), 0);
    setForm(p => {
      const hasAny = scores.some(k => p[k] !== '' && p[k] !== undefined);
      if (!hasAny) return p;
      return { ...p, trauma_total: String(total) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.trauma_gcs, form.trauma_pas, form.trauma_fr, form.trauma_esf, form.trauma_llen]);

  const set = useCallback((name, value) => {
    if (readOnly) return;
    setForm(p => ({ ...p, [name]: value }));
  }, [readOnly]);

  // Admin-only: fills form with realistic test data
  const fillTestData = useCallback(() => {
    setForm(p => ({
      ...p,
      fecha: getLocalDate(),
      hora_llamada: '14:30', hora_salida: '14:31', hora_llegada: '14:44',
      hora_traslado: '15:05', hora_hospital: '15:10', hora_base: '15:30',

      motivo_atencion: 'TRAUMA',
      incidente_calle: 'Av. Las Palmas 500', incidente_colonia: 'Valle Dorado', incidente_delegacion: 'Ensenada',
      lugar_ocurrencia_vp: true, lugar_ocurrencia_hogar: false, lugar_ocurrencia_trabajo: false,
      lugar_ocurrencia_escuela: false, lugar_ocurrencia_recreativo: false, lugar_ocurrencia_otro: false, lugar_ocurrencia_otro_texto: '',
      operador: 'José Sánchez', prestadores_servicio: 'TUM González, TUM Ruiz',

      nombre_paciente: 'Juan Carlos Pérez Martínez', nombre_acompanante: 'María Luisa Soto',
      edad_anios: '42', edad_meses: '0', sexo: 'MASC', telefono: '6461234567',
      domicilio: 'Av. Reforma 1234', colonia: 'Centro', delegacion: 'Ensenada Centro',
      ocupacion: 'Comerciante',
      derechohabiente: 'IMSS', compania_seguros: 'Quálitas',

      origen_neurologia: false, origen_digestiva: false, origen_intoxicacion: true,
      origen_cardiovascular: false, origen_urogenital: false, origen_infecciosa: false,
      origen_respiratorio: true, origen_gineco: false, origen_oncologico: false,
      origen_metabolico: false, origen_cognitivo: false, origen_otro: false,
      origen_especifique: 'Inhalación humo',
      primera_vez: true, subsecuente: false,

      acc_colision: true, acc_volcadura: false,
      acc_col_automotor: true, acc_col_moto: false, acc_col_bici: false, acc_col_maquinaria: false,
      acc_vol_automotor: false, acc_vol_moto: false, acc_vol_bici: false, acc_vol_maquinaria: false,
      contra_fijo: true, contra_movimiento: false,
      impacto_frontal: true, impacto_lateral: false, impacto_posterior: false,
      hundimiento_cms: '30',
      parabrisas_roto: true, parabrisas_doblado: false,
      volante_integro: false, volante_doblado: true,
      bolsa_si: true, bolsa_no: false,
      cinturon_colocado: false, cinturon_no: true,
      dentro_si: true, dentro_no: false, eyectado: false,
      atrop_automotor: false, atrop_moto: false, atrop_bici: false, atrop_maquinaria: false,
      casco_si: false, casco_no: true,

      agente_arma: false, agente_maquinaria: false, agente_electricidad: false,
      agente_juguete: false, agente_herramienta: false, agente_explosion: false,
      agente_automotor: true, agente_fuego: false, agente_ser_humano: false,
      agente_bicicleta: false, agente_sust_caliente: false, agente_animal: false,
      agente_prod_biologico: false, agente_sust_toxica: false, agente_otro: false,
      agente_especifique: '', lesiones_causadas_por: 'Tablero y volante',

      cond_critico_inestable: true, cond_critico_estable: false, cond_no_critico: false,
      prioridad: 'ROJO',

      via_aspiracion: false, via_orofaringea: true, via_nasofaringea: false,
      cervical_manual: true, cervical_rigido: true, cervical_blando: false,

      vent_balon: false, vent_auto: false,
      vent_frec: '22', vent_vol: '',
      vent_puntas: false, vent_mascarilla_simple: false, vent_mascarilla_res: true,
      vent_lts_min: '15',

      hem_presion_directa: true, hem_torniquete: false, hem_vendaje: true,
      hem_via_iv: 'Periférica', hem_cateter: '18G',
      hem_sitio_mano: false, hem_sitio_pliegue: true,
      sol_hartmann: true, sol_nacl: false, sol_mixta: false, sol_glucosa: false, sol_otra: false,

      alergias: 'Penicilina', medicamentos: 'Metformina 850mg',
      enfermedades_previas: 'Diabetes tipo 2, Hipertensión',
      hora_ultima_comida: '12:00', eventos_previos: 'Desmayo previo al impacto',

      sv_rows: [
        { hora: '14:44', fc: '102', fr: '22', tas: '145', tad: '92', temp: '36.8', spo2: '94', glucosa: '210', ekg: 'Sinusal', pupilas: 'Isocóricas', piel: 'Diaforética', llenado_cap: '2-4 seg', dolor: '7' },
        { hora: '15:00', fc: '98', fr: '20', tas: '138', tad: '88', temp: '36.6', spo2: '96', glucosa: '195', ekg: 'Sinusal', pupilas: 'Isocóricas', piel: 'Pálida', llenado_cap: '< 2 seg', dolor: '5' },
      ],

      observaciones: 'Paciente masculino de 42 años involucrado en colisión frontal con poste. No portaba cinturón, volante deformado. Presenta disnea, dolor torácico (7/10) y escoriaciones en brazos. Se coloca collarín cervical rígido y oxígeno a 15 L/min por mascarilla con reservorio. Se canaliza vía periférica en pliegue derecho con catéter 18G administrando sol. Hartmann 500cc. Traslado urgente a IMSS.',
      glasgow_ocular: '3', glasgow_verbal: '4', glasgow_motor: '5', glasgow_total: '12',
      cincinnati_asimetria_facial: 'NO',
      cincinnati_paresia_brazos: 'SI',
      cincinnati_alteracion_lenguaje: 'NO',
      trauma_gcs: '11-13', trauma_pas: '>90', trauma_fr: '10-24', trauma_esf: 'Normal', trauma_llen: 'Normal',
      trauma_total: '18', // mock total calculation logic trigger

      destino_trasladado: true, destino_no_trasladado: false, destino_fuga: false,

      lesion_zonas: ['torax', 'abdomen', 'hombro_der', 'brazo_der_d', 'cara'],

      gesta: '3', cesareas: '1', para: '2', abortos: '0', fum: '15/01/24',
      semanas_gestacion: '38', fecha_probable_parto: '20/10/24',
      hora_contracciones: '13:00', frec_contracciones: '5 min', dur_contracciones: '30 seg',
      hora_nacimiento: '14:50', lugar_nacimiento: 'Ambulancia',
      placenta_expulsada: true,
      prod_vivo: true, prod_muerto: false,
      recien_sexo: 'FEM',
      apgar_apariencia: '2', apgar_pulso: '2', apgar_gesticulacion: '1', apgar_actividad: '2', apgar_respiracion: '2', apgar_total: '9',
      prod_texto: 'Recién nacido vigoroso, llanto fuerte.',

      nombre_pac_negativa: 'Firma Paciente Negativa',
      nombre_testigo: 'Firma Testigo',
      nombre_pac_consentimiento: 'Juan Carlos Pérez Martínez',
      nombre_familiar: 'María Luisa Soto',
      nombre_entrega: 'TUM López',
      nombre_medico: 'Dr. García',
      creado_por: 'TUM Admin',
    }));
  }, []);


  const setMulti = useCallback((updates) => {
    if (readOnly) return;
    setForm(p => ({ ...p, ...updates }));
  }, [readOnly]);

  const addSvRow = useCallback(() => {
    if (readOnly) return;
    setForm(p => ({
      ...p,
      sv_rows: [...(p.sv_rows || []), { hora: '', fc: '', fr: '', tas: '', tad: '', temp: '', spo2: '', glucosa: '', ekg: '', pupilas: '', piel: '', llenado_cap: '', dolor: '' }],
    }));
  }, [readOnly]);

  const setSvRow = useCallback((idx, field, value) => {
    if (readOnly) return;
    setForm(p => {
      const rows = [...(p.sv_rows || [])];
      rows[idx] = { ...rows[idx], [field]: value };
      return { ...p, sv_rows: rows };
    });
  }, [readOnly]);

  const removeSvRow = useCallback((idx) => {
    if (readOnly) return;
    setForm(p => {
      const rows = [...(p.sv_rows || [])];
      if (rows.length <= 1) return p;
      rows.splice(idx, 1);
      return { ...p, sv_rows: rows };
    });
  }, [readOnly]);

  const scrollToSection = (id) => {
    const el = document.getElementById(`sec-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(id);
    setNavOpen(false);
  };

  const handleSave = async () => {
    setSaveError('');
    setSaving(true);
    setSaveSuccess(false);
    try {
      if (isEdit) {
        await updateFrap(editId, form);
      } else {
        const result = await createFrap(form);
        // Update form with the real folio assigned by server
        setForm(p => ({ ...p, folio: result.folio }));
        // Navigate to edit URL so subsequent saves update instead of create
        navigate(`/frap/editar/${result.id}`, { replace: true });
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePdf = async () => {
    setPdfError('');
    setGenerating(true);
    try {
      const pdfBytes = await generateFrapPdf(form);
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FRAP-${form.folio}-${form.nombre_paciente || 'paciente'}-${form.fecha}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setPdfError('Error al generar PDF. Intente de nuevo.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-[#1a3a5c] border-b border-white/10 shadow-md">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 flex items-center gap-3 h-14">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1 text-slate-300 hover:text-white text-sm transition-colors flex-shrink-0" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Volver</span>
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <img src={LOGO_URL} alt="Logo" className="h-8 w-8 rounded-full object-cover flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-white font-black text-sm leading-tight truncate" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {readOnly ? 'Viendo FRAP' : isEdit ? 'Editando FRAP' : 'Nuevo FRAP'}
              </p>
              <p className="text-green-400 text-xs font-bold leading-tight">
                {form.folio ? (form.folio.startsWith('~') ? `Folio aprox. #${form.folio.slice(1)}` : `Folio #${form.folio}`) : 'Sin guardar'}
              </p>
            </div>
          </div>
          {/* Mobile nav toggle */}
          <button onClick={() => setNavOpen(!navOpen)} className="sm:hidden text-slate-300 hover:text-white text-xs border border-white/20 px-2 py-1 rounded" data-testid="nav-toggle-btn">
            Secciones
          </button>
          {saveSuccess && <span className="hidden sm:flex items-center gap-1 text-green-300 text-xs"><CheckCircle className="w-3.5 h-3.5" /> Guardado</span>}
          {saveError && <p className="text-red-300 text-xs hidden sm:block">{saveError}</p>}
          {!readOnly && user?.role === 'admin' && (
            <button
              type="button"
              onClick={fillTestData}
              className="flex items-center gap-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-400/40 font-bold text-xs px-3 py-2 rounded-md transition-all flex-shrink-0"
              title="Solo administradores"
              data-testid="autofill-btn"
            >
              ⚡ Autorrellenar
            </button>
          )}
          {!readOnly && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 disabled:opacity-50 text-white font-bold text-xs px-3 py-2 rounded-md transition-all border border-white/20 flex-shrink-0"
              data-testid="save-frap-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{saving ? 'Guardando...' : 'Guardar'}</span>
            </button>
          )}
          <button
            onClick={handleGeneratePdf}
            disabled={generating}
            className="flex items-center gap-2 bg-[#1a7a4a] hover:bg-[#16a360] disabled:opacity-60 text-white font-black text-xs px-4 py-2.5 rounded-md transition-all uppercase tracking-wide active:scale-95 flex-shrink-0 shadow-sm"
            data-testid="generate-pdf-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            <span className="hidden sm:inline">{generating ? 'Generando...' : 'Generar PDF'}</span>
          </button>
        </div>
        {/* Mobile nav dropdown */}
        {navOpen && (
          <div className="sm:hidden bg-[#0d2839] border-t border-white/10 p-3 grid grid-cols-2 gap-1.5">
            {SECTIONS.map(s => (
              <button key={s.id} onClick={() => scrollToSection(s.id)} className="text-left text-xs text-slate-300 hover:text-white hover:bg-white/10 px-2 py-1.5 rounded transition-colors flex items-center gap-1">
                <ChevronRight className="w-3 h-3" />{s.label}
              </button>
            ))}
          </div>
        )}
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 flex gap-6">
        {/* Sidebar nav (desktop) */}
        <aside className="hidden sm:block w-44 flex-shrink-0">
          <div className="sticky top-20 bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <div className="bg-[#1a3a5c] px-3 py-2">
              <p className="text-white text-[10px] font-bold uppercase tracking-widest">Secciones</p>
            </div>
            <nav className="p-1">
              {SECTIONS.map(s => (
                <button
                  key={s.id}
                  onClick={() => scrollToSection(s.id)}
                  className={`w-full text-left text-xs px-3 py-2 rounded transition-colors flex items-center gap-1.5 ${activeSection === s.id ? 'bg-[#1a7a4a]/10 text-[#1a7a4a] font-bold' : 'text-slate-600 hover:bg-slate-50 hover:text-[#1a3a5c]'}`}
                  data-testid={`nav-${s.id}`}
                >
                  {activeSection === s.id && <div className="w-1 h-3 bg-[#1a7a4a] rounded-full flex-shrink-0" />}
                  {s.label}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main form */}
        <div className="flex-1 min-w-0 space-y-4">
          {(pdfError || saveError) && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg" data-testid="form-error">
              <AlertCircle className="w-4 h-4" />{pdfError || saveError}
            </div>
          )}
          {saveSuccess && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-lg" data-testid="save-success">
              <CheckCircle className="w-4 h-4" /> FRAP guardado correctamente
            </div>
          )}

          {/* ── INCIDENTE ── */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <SH id="incidente" title="Datos del Incidente" />
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <label className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest"># Folio</label>
                  <div className="border-b border-slate-300 py-0.5">
                    <span className="text-xl font-black text-[#1a3a5c] tracking-wider" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="folio-display">
                      {form.folio ? (form.folio.startsWith('~') ? `≈${form.folio.slice(1)}` : form.folio) : '---'}
                    </span>
                    {form.folio?.startsWith('~') && (
                      <span className="ml-2 text-[9px] text-amber-500 font-semibold">Guardar para confirmar</span>
                    )}
                  </div>
                </div>
                <F label="Fecha" name="fecha" value={form.fecha} onChange={set} type="date" />
                <div></div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {[
                  ['Hora Llamada', 'hora_llamada'], ['Hora Salida', 'hora_salida'], ['Hora Llegada', 'hora_llegada'],
                  ['Hora Traslado', 'hora_traslado'], ['Hora Hospital', 'hora_hospital'], ['Hora Base', 'hora_base'],
                ].map(([lbl, n]) => (
                  <F key={n} label={lbl} name={n} value={form[n]} onChange={set} type="time" testId={`field-${n}`} />
                ))}
              </div>
              <div className="pt-3 border-t border-slate-100 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest block mb-1.5">Motivo de la Atención</label>
                    <div className="flex flex-wrap gap-3">
                      {['TRAUMA', 'ENFERMEDAD', 'GINECO-OBST.', 'PSIQUIÁTRICO', 'OTRO'].map(opt => (
                        <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                          <input type="radio" name="motivo_atencion" value={opt} checked={form.motivo_atencion === opt} onChange={() => set('motivo_atencion', opt)} className="accent-[#1a7a4a]" />
                          <span className="text-xs font-medium uppercase text-slate-700">{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest block mb-1.5">Lugar de Ocurrencia</label>
                    <div className="grid grid-cols-2 gap-2">
                      <CB label="Vía Pública" name="lugar_ocurrencia_vp" checked={form.lugar_ocurrencia_vp} onChange={set} />
                      <CB label="Hogar" name="lugar_ocurrencia_hogar" checked={form.lugar_ocurrencia_hogar} onChange={set} />
                      <CB label="Trabajo" name="lugar_ocurrencia_trabajo" checked={form.lugar_ocurrencia_trabajo} onChange={set} />
                      <CB label="Escuela" name="lugar_ocurrencia_escuela" checked={form.lugar_ocurrencia_escuela} onChange={set} />
                      <CB label="Centro Recreativo" name="lugar_ocurrencia_recreativo" checked={form.lugar_ocurrencia_recreativo} onChange={set} />
                      <div className="flex items-center gap-2">
                        <CB label="Otro" name="lugar_ocurrencia_otro" checked={form.lugar_ocurrencia_otro} onChange={(name, val) => { set(name, val); if (!val) set('lugar_ocurrencia_otro_texto', ''); }} />
                        {form.lugar_ocurrencia_otro && (
                          <input
                            type="text"
                            value={form.lugar_ocurrencia_otro_texto || ''}
                            onChange={e => set('lugar_ocurrencia_otro_texto', e.target.value)}
                            placeholder="Especifique..."
                            className="border-b border-[#1a7a4a] bg-transparent focus:outline-none text-xs py-0.5 px-1 font-medium text-slate-900 placeholder:text-slate-400 w-full"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <F label="Calle / Ubicación" name="incidente_calle" value={form.incidente_calle} onChange={set} />
                  <F label="Colonia" name="incidente_colonia" value={form.incidente_colonia} onChange={set} />
                  <F label="Delegación / Municipio" name="incidente_delegacion" value={form.incidente_delegacion} onChange={set} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-2">
                  <F label="Operador" name="operador" value={form.operador} onChange={set} />
                  <F label="Prestadores de Servicio" name="prestadores_servicio" value={form.prestadores_servicio} onChange={set} />
                </div>
              </div>
            </div>
          </div>

          {/* ── NOMBRE DEL PACIENTE ── */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <SH id="paciente" title="Nombre del Paciente" />
            <div className="p-4 space-y-3">
              <F label="Nombre del Paciente" name="nombre_paciente" value={form.nombre_paciente} onChange={set} className="col-span-full" />
              <F label="Nombre del Acompañante" name="nombre_acompanante" value={form.nombre_acompanante} onChange={set} />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <label className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest block mb-1.5">Sexo</label>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="sexo" value="MASC" checked={form.sexo === 'MASC'} onChange={() => set('sexo', 'MASC')} className="accent-[#1a7a4a]" data-testid="sexo-masc" />
                      <span className="text-xs font-medium uppercase text-slate-700">Masc.</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="sexo" value="FEM" checked={form.sexo === 'FEM'} onChange={() => set('sexo', 'FEM')} className="accent-[#1a7a4a]" data-testid="sexo-fem" />
                      <span className="text-xs font-medium uppercase text-slate-700">Fem.</span>
                    </label>
                  </div>
                </div>
                <F label="Edad (Años)" name="edad_anios" value={form.edad_anios} onChange={set} type="number" min="0" max="130" />
                <F label="Edad (Meses)" name="edad_meses" value={form.edad_meses} onChange={set} type="number" min="0" max="11" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <F label="Domicilio" name="domicilio" value={form.domicilio} onChange={set} />
                <F label="Colonia / Comunidad" name="colonia" value={form.colonia} onChange={set} />
              </div>
              <F label="Delegación Política / Municipio" name="delegacion" value={form.delegacion} onChange={set} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <F label="Teléfono" name="telefono" value={form.telefono} onChange={set} type="tel" />
                <F label="Ocupación" name="ocupacion" value={form.ocupacion} onChange={set} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <F label="Derechohabiente A" name="derechohabiente" value={form.derechohabiente} onChange={set} />
                <F label="Compañía de Seguros Gastos Médicos" name="compania_seguros" value={form.compania_seguros} onChange={set} />
              </div>
            </div>
          </div>

          {/* ── ORIGEN PROBABLE ── */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <SH id="origen" title="Origen Probable" />
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  ['Neurología', 'origen_neurologia'], ['Digestiva', 'origen_digestiva'], ['Intoxicación', 'origen_intoxicacion'],
                  ['Cardiovascular', 'origen_cardiovascular'], ['Urogenital', 'origen_urogenital'], ['Infecciosa', 'origen_infecciosa'],
                  ['Respiratorio', 'origen_respiratorio'], ['Gineco-Obstétrica', 'origen_gineco'], ['Oncológico', 'origen_oncologico'],
                  ['Metabólico', 'origen_metabolico'], ['Cognitivo Emocional', 'origen_cognitivo'], ['Otro', 'origen_otro'],
                ].map(([lbl, n]) => <CB key={n} label={lbl} name={n} checked={form[n]} onChange={set} />)}
              </div>
              <F label="Especifique" name="origen_especifique" value={form.origen_especifique} onChange={set} />
              <div className="flex gap-6">
                <CB label="1a Vez" name="primera_vez" checked={form.primera_vez} onChange={set} />
                <CB label="Subsecuente" name="subsecuente" checked={form.subsecuente} onChange={set} />
              </div>
            </div>
          </div>

          {/* ── ACCIDENTE AUTOMOVILÍSTICO ── */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <SH id="accidente" title="Accidente Automovilístico" />
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pb-2 border-b border-slate-100">
                <CB label="Colisión" name="acc_colision" checked={form.acc_colision} onChange={set} />
                <CB label="Volcadura" name="acc_volcadura" checked={form.acc_volcadura} onChange={set} />
              </div>
              <div>
                <p className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest mb-2">Vehículo Involucrado</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[['Automotor', 'acc_col_automotor'], ['Motocicleta', 'acc_col_moto'], ['Bicicleta', 'acc_col_bici'], ['Maquinaria', 'acc_col_maquinaria']].map(([lbl, n]) => <CB key={n} label={lbl} name={n} checked={form[n]} onChange={set} />)}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2 border-t border-slate-100">
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Contra Objeto</p>
                  <div className="flex flex-col gap-1.5">
                    <CB label="Fijo" name="contra_fijo" checked={form.contra_fijo} onChange={set} />
                    <CB label="En Movimiento" name="contra_movimiento" checked={form.contra_movimiento} onChange={set} />
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Impacto</p>
                  <div className="flex flex-col gap-1.5">
                    <CB label="Frontal" name="impacto_frontal" checked={form.impacto_frontal} onChange={set} />
                    <CB label="Lateral" name="impacto_lateral" checked={form.impacto_lateral} onChange={set} />
                    <CB label="Posterior" name="impacto_posterior" checked={form.impacto_posterior} onChange={set} />
                  </div>
                </div>
                <div>
                  <F label="Hundimiento (cms)" name="hundimiento_cms" value={form.hundimiento_cms} onChange={set} type="number" />
                  <div className="mt-2 flex flex-col gap-1">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Parabrisas</p>
                    <CB label="Roto" name="parabrisas_roto" checked={form.parabrisas_roto} onChange={set} />
                    <CB label="Doblado" name="parabrisas_doblado" checked={form.parabrisas_doblado} onChange={set} />
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Volante</p>
                  <CB label="Íntegro" name="volante_integro" checked={form.volante_integro} onChange={set} />
                  <CB label="Doblado" name="volante_doblado" checked={form.volante_doblado} onChange={set} />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2 border-t border-slate-100">
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Bolsa de Aire</p>
                  <div className="flex gap-3">
                    <CB label="Sí" name="bolsa_si" checked={form.bolsa_si} onChange={set} />
                    <CB label="No" name="bolsa_no" checked={form.bolsa_no} onChange={set} />
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Cinturón de Seguridad</p>
                  <CB label="Colocado" name="cinturon_colocado" checked={form.cinturon_colocado} onChange={set} />
                  <CB label="No Colocado" name="cinturon_no" checked={form.cinturon_no} onChange={set} />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Dentro del Vehículo</p>
                  <CB label="Sí" name="dentro_si" checked={form.dentro_si} onChange={set} />
                  <CB label="No" name="dentro_no" checked={form.dentro_no} onChange={set} />
                  <CB label="Eyectado" name="eyectado" checked={form.eyectado} onChange={set} />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Casco de Seguridad</p>
                  <div className="flex gap-3">
                    <CB label="Sí" name="casco_si" checked={form.casco_si} onChange={set} />
                    <CB label="No" name="casco_no" checked={form.casco_no} onChange={set} />
                  </div>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Atropellado por</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <CB label="Automotor" name="atrop_automotor" checked={form.atrop_automotor} onChange={set} />
                  <CB label="Motocicleta" name="atrop_moto" checked={form.atrop_moto} onChange={set} />
                  <CB label="Bicicleta" name="atrop_bici" checked={form.atrop_bici} onChange={set} />
                  <CB label="Maquinaria" name="atrop_maquinaria" checked={form.atrop_maquinaria} onChange={set} />
                </div>
              </div>
            </div>
          </div>

          {/* ── ZONA DE LESIÓN ── */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <SH id="zona-lesion" title="Zona de Lesión" />
            <div className="p-4">
              <BodyDiagram
                value={form.lesion_zonas || []}
                onChange={zonas => set('lesion_zonas', zonas)}
                readOnly={readOnly}
              />
            </div>
          </div>

          {/* ── AGENTE CAUSAL ── */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <SH id="agente" title="Agente Causal" />
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  ['Arma', 'agente_arma'], ['Maquinaria', 'agente_maquinaria'], ['Electricidad', 'agente_electricidad'],
                  ['Juguete', 'agente_juguete'], ['Herramienta', 'agente_herramienta'], ['Explosión', 'agente_explosion'],
                  ['Automotor', 'agente_automotor'], ['Fuego', 'agente_fuego'], ['Ser Humano', 'agente_ser_humano'],
                  ['Bicicleta', 'agente_bicicleta'], ['Sustancia Caliente', 'agente_sust_caliente'], ['Animal', 'agente_animal'],
                  ['Producto Biológico', 'agente_prod_biologico'], ['Sustancia Tóxica', 'agente_sust_toxica'], ['Otro', 'agente_otro'],
                ].map(([lbl, n]) => <CB key={n} label={lbl} name={n} checked={form[n]} onChange={set} />)}
              </div>
              <F label="Especifique" name="agente_especifique" value={form.agente_especifique} onChange={set} />
              <F label="Lesiones Causadas Por" name="lesiones_causadas_por" value={form.lesiones_causadas_por} onChange={set} />
            </div>
          </div>

          {/* ── CONDICIÓN / PRIORIDAD ── */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <SH id="condicion" title="Condición del Paciente / Prioridad" />
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest mb-2">Condición del Paciente</p>
                <div className="flex flex-col gap-2">
                  <CB label="Crítico Inestable" name="cond_critico_inestable" checked={form.cond_critico_inestable} onChange={set} />
                  <CB label="Crítico Estable" name="cond_critico_estable" checked={form.cond_critico_estable} onChange={set} />
                  <CB label="No Crítico" name="cond_no_critico" checked={form.cond_no_critico} onChange={set} />
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest mb-2">Prioridad</p>
                <div className="flex flex-wrap gap-2" data-testid="prioridad-group">
                  <RadioBtn label="Rojo" name="prioridad" value="ROJO" current={form.prioridad} onChange={set} />
                  <RadioBtn label="Verde" name="prioridad" value="VERDE" current={form.prioridad} onChange={set} />
                  <RadioBtn label="Amarillo" name="prioridad" value="AMARILLO" current={form.prioridad} onChange={set} />
                  <RadioBtn label="Negro" name="prioridad" value="NEGRO" current={form.prioridad} onChange={set} />
                </div>
              </div>
            </div>
          </div>

          {/* ── VÍA AÉREA / CONTROL CERVICAL ── */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <SH id="via-aerea" title="Vía Aérea / Control Cervical" />
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest mb-2">Vía Aérea</p>
                <div className="flex flex-col gap-1.5">
                  <CB label="Aspiración" name="via_aspiracion" checked={form.via_aspiracion} onChange={set} />
                  <CB label="Cánula Orofaringea" name="via_orofaringea" checked={form.via_orofaringea} onChange={set} />
                  <CB label="Cánula Nasofaringea" name="via_nasofaringea" checked={form.via_nasofaringea} onChange={set} />
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest mb-2">Control Cervical</p>
                <div className="flex flex-col gap-1.5">
                  <CB label="Manual" name="cervical_manual" checked={form.cervical_manual} onChange={set} />
                  <CB label="Collarín Rígido" name="cervical_rigido" checked={form.cervical_rigido} onChange={set} />
                  <CB label="Collarín Blando" name="cervical_blando" checked={form.cervical_blando} onChange={set} />
                </div>
              </div>
            </div>
          </div>

          {/* ── ASISTENCIA VENTILATORIA / OXIGENOTERAPIA ── */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <SH id="ventilacion" title="Asistencia Ventilatoria / Oxigenoterapia" />
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <CB label="Balón-Válvula Mascarilla" name="vent_balon" checked={form.vent_balon} onChange={set} />
                <CB label="Ventilador Automático" name="vent_auto" checked={form.vent_auto} onChange={set} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <F label="Frec." name="vent_frec" value={form.vent_frec} onChange={set} />
                <F label="Vol." name="vent_vol" value={form.vent_vol} onChange={set} />
                <F label="LTS x MIN" name="vent_lts_min" value={form.vent_lts_min} onChange={set} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <CB label="Puntas Nasales" name="vent_puntas" checked={form.vent_puntas} onChange={set} />
                <CB label="Mascarilla Simple" name="vent_mascarilla_simple" checked={form.vent_mascarilla_simple} onChange={set} />
                <CB label="Mascarilla con Reservorio" name="vent_mascarilla_res" checked={form.vent_mascarilla_res} onChange={set} />
              </div>
            </div>
          </div>

          {/* ── CONTROL DE HEMORRAGIAS ── */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <SH id="hemorragias" title="Control de Hemorragias / Vías Venosas" />
            <div className="p-4 space-y-3">
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Control de Hemorragias</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <CB label="Presión Directa" name="hem_presion_directa" checked={form.hem_presion_directa} onChange={set} />
                  <CB label="Torniquete" name="hem_torniquete" checked={form.hem_torniquete} onChange={set} />
                  <CB label="Vendaje Compresivo" name="hem_vendaje" checked={form.hem_vendaje} onChange={set} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <F label="Vía IV #" name="hem_via_iv" value={form.hem_via_iv} onChange={set} />
                <F label="Catéter #" name="hem_cateter" value={form.hem_cateter} onChange={set} />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Sitio de Aplicación</p>
                <div className="flex gap-4">
                  <CB label="Mano" name="hem_sitio_mano" checked={form.hem_sitio_mano} onChange={set} />
                  <CB label="Pliegue Antecubital" name="hem_sitio_pliegue" checked={form.hem_sitio_pliegue} onChange={set} />
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Tipo de Soluciones</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <CB label="Hartmann" name="sol_hartmann" checked={form.sol_hartmann} onChange={set} />
                  <CB label="NaCl 0.9%" name="sol_nacl" checked={form.sol_nacl} onChange={set} />
                  <CB label="Mixta" name="sol_mixta" checked={form.sol_mixta} onChange={set} />
                  <CB label="Glucosa 5%" name="sol_glucosa" checked={form.sol_glucosa} onChange={set} />
                  <CB label="Otra" name="sol_otra" checked={form.sol_otra} onChange={set} />
                </div>
              </div>
            </div>
          </div>

          {/* ── INTERROGATORIO ── */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <SH id="interrogatorio" title="Interrogatorio" />
            <div className="p-4 space-y-3">
              <F label="Alergias" name="alergias" value={form.alergias} onChange={set} />
              <F label="Medicamentos que está ingiriendo" name="medicamentos" value={form.medicamentos} onChange={set} />
              <F label="Enfermedades y cirugías previas" name="enfermedades_previas" value={form.enfermedades_previas} onChange={set} />
              <F label="Hora de la última comida" name="hora_ultima_comida" value={form.hora_ultima_comida} onChange={set} />
              <F label="Eventos previos relacionados" name="eventos_previos" value={form.eventos_previos} onChange={set} />
            </div>
          </div>

          {/* ── SIGNOS VITALES ── */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <SH id="signos-vitales" title="Signos Vitales" />
            <div className="p-4 space-y-3">
              <p className="text-[10px] text-slate-500 font-medium">Registre los signos vitales del paciente. Puede agregar múltiples tomas.</p>

              {/* ── MOBILE CARD VIEW (hidden on sm+) ── */}
              <div className="sm:hidden space-y-3">
                {/* Toma navigator */}
                {(form.sv_rows || []).length > 1 && (
                  <div className="flex gap-1.5 overflow-x-auto pb-1">
                    {(form.sv_rows || []).map((_, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setForm(p => ({ ...p, _svActive: idx }))}
                        className={`flex-shrink-0 text-xs font-bold px-3 py-1.5 rounded-full border-2 transition-all ${(form._svActive ?? 0) === idx ? 'bg-[#1a3a5c] border-[#1a3a5c] text-white' : 'border-slate-300 text-slate-600'}`}
                      >
                        Toma {idx + 1}
                      </button>
                    ))}
                  </div>
                )}

                {/* Active toma card */}
                {(() => {
                  const idx = form._svActive ?? 0;
                  const row = (form.sv_rows || [])[idx] || {};
                  const selectOptions = (min, max, step) => {
                    const opts = [];
                    for (let i = min; i <= max; i += step) {
                      opts.push(Number.isInteger(step) ? i : i.toFixed(1));
                    }
                    return opts;
                  };

                  const getOptionsForKey = (key) => {
                    switch (key) {
                      case 'fc': return selectOptions(40, 200, 5); // 40 to 200 in steps of 5
                      case 'fr': return selectOptions(8, 50, 2);  // 8 to 50 in steps of 2
                      case 'tas': return selectOptions(60, 250, 10); // 60 to 250 in steps of 10
                      case 'tad': return selectOptions(40, 150, 5);  // 40 to 150 in steps of 5
                      case 'temp': return selectOptions(35.0, 41.0, 0.1); // 35.0 to 41.0 in steps of 0.1
                      case 'spo2': return selectOptions(70, 100, 1).reverse(); // 100 down to 70
                      case 'glucosa': return selectOptions(40, 500, 10); // 40 to 500 in steps of 10
                      case 'dolor': return selectOptions(0, 10, 1);    // 0 to 10 in steps of 1
                      default: return [];
                    }
                  };

                  const numField = (label, key, unit) => (
                    <div key={key} className="bg-slate-50 rounded-xl p-3 flex flex-col gap-1.5">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-wider">{label}</p>
                        <span className="text-[9px] text-slate-400 font-medium">{unit}</span>
                      </div>
                      <div className="relative">
                        <select
                          value={row[key] || ''}
                          onChange={e => setSvRow(idx, key, e.target.value)}
                          disabled={readOnly}
                          className="w-full appearance-none bg-white border-2 border-slate-200 rounded-xl px-3 py-2.5 text-lg font-black text-center text-[#1a3a5c] focus:border-[#1a7a4a] focus:outline-none disabled:bg-slate-100 transition-colors cursor-pointer"
                          data-testid={`sv-${idx}-${key}`}
                        >
                          <option value="" disabled>--</option>
                          {getOptionsForKey(key).map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center px-1 text-slate-400">
                          ▼
                        </div>
                      </div>
                    </div>
                  );
                  const chipField = (label, key, opts) => (
                    <div key={key} className="bg-slate-50 rounded-xl p-3">
                      <p className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-wider mb-2">{label}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {opts.map(v => (
                          <button key={v} type="button" disabled={readOnly}
                            onClick={() => setSvRow(idx, key, row[key] === v ? '' : v)}
                            className={`text-[11px] font-bold px-2.5 py-1.5 rounded-lg border-2 transition-all active:scale-95 ${row[key] === v ? 'bg-[#1a7a4a] border-[#1a7a4a] text-white' : 'bg-white border-slate-200 text-slate-700'} disabled:opacity-50`}
                          >{v}</button>
                        ))}
                      </div>
                    </div>
                  );
                  return (
                    <div className="border-2 border-[#1a3a5c]/10 rounded-2xl overflow-hidden">
                      {/* Card header */}
                      <div className="bg-[#1a3a5c] px-4 py-2.5 flex items-center justify-between">
                        <p className="text-white font-black text-sm">Toma {idx + 1}</p>
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col">
                            <label className="text-[9px] text-white/60 uppercase font-bold">Hora</label>
                            <input
                              type="time"
                              value={row.hora || ''}
                              onChange={e => setSvRow(idx, 'hora', e.target.value)}
                              disabled={readOnly}
                              className="bg-white/10 text-white text-sm font-bold border-0 focus:outline-none rounded px-1 w-24"
                              data-testid={`sv-${idx}-hora`}
                            />
                          </div>
                          {!readOnly && (form.sv_rows || []).length > 1 && (
                            <button type="button" onClick={() => { removeSvRow(idx); setForm(p => ({ ...p, _svActive: Math.max(0, idx - 1) })); }}
                              className="text-red-300 hover:text-red-100 text-xs font-black px-2 py-1 rounded border border-red-300/30 hover:bg-red-500/20 transition-all"
                              data-testid={`sv-remove-${idx}`}
                            >✕ Eliminar</button>
                          )}
                        </div>
                      </div>
                      {/* Numeric fields grid */}
                      <div className="p-3 grid grid-cols-2 gap-2 bg-white">
                        {numField('Frec. Cardíaca', 'fc', 'lpm')}
                        {numField('Frec. Respiratoria', 'fr', 'rpm')}
                        {numField('TAS', 'tas', 'mmHg')}
                        {numField('TAD', 'tad', 'mmHg')}
                        {numField('Temperatura', 'temp', '°C')}
                        {numField('SpO2', 'spo2', '%')}
                        {numField('Glucosa', 'glucosa', 'mg/dL')}
                        {numField('Dolor', 'dolor', '0–10')}
                      </div>
                      {/* Chip fields */}
                      <div className="px-3 pb-3 grid grid-cols-1 gap-2 bg-white">
                        {chipField('Pupilas', 'pupilas', ['Isocóricas', 'Anisocóricas', 'Midriasis', 'Miosis', 'Reactivas', 'No Reactivas'])}
                        {chipField('Piel', 'piel', ['Normal', 'Pálida', 'Cianótica', 'Diaforética', 'Caliente', 'Fría', 'Seca', 'Húmeda'])}
                        {chipField('Llenado Capilar', 'llenado_cap', ['< 2 seg', '2-4 seg', '> 4 seg', 'Ausente'])}
                        {chipField('EKG', 'ekg', ['Sinusal', 'Taquicardia', 'Bradicardia', 'Fibrilación', 'Asistolia'])}
                      </div>
                    </div>
                  );
                })()}

                {/* Add toma button */}
                {!readOnly && (
                  <button type="button"
                    onClick={() => { addSvRow(); setForm(p => ({ ...p, _svActive: (p.sv_rows || []).length })); }}
                    className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#1a7a4a] text-[#1a7a4a] font-black text-sm py-3 rounded-xl hover:bg-[#1a7a4a]/5 active:scale-95 transition-all"
                    data-testid="sv-add-row-btn-mobile"
                  >
                    + Agregar Toma de Signos Vitales
                  </button>
                )}
              </div>

              {/* ── DESKTOP TABLE VIEW (hidden on mobile) ── */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-xs border-collapse" data-testid="signos-vitales-table">
                  <thead>
                    <tr className="bg-[#1a3a5c] text-white">
                      {['Hora', 'FC', 'FR', 'TAS', 'TAD', 'Temp °C', 'SpO2 %', 'Glucosa', 'EKG', 'Pupilas', 'Piel', 'Llen.Cap', 'Dolor', ''].map((h, i) => (
                        <th key={i} className="px-1.5 py-1.5 text-[9px] font-bold uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(form.sv_rows || []).map((row, idx) => {
                      const getOptionsForKey = (key) => {
                        const selectOptions = (min, max, step) => {
                          const opts = [];
                          for (let i = min; i <= max; i += step) opts.push(Number.isInteger(step) ? i : i.toFixed(1));
                          return opts;
                        };
                        switch (key) {
                          case 'fc': return selectOptions(40, 200, 5);
                          case 'fr': return selectOptions(8, 50, 2);
                          case 'tas': return selectOptions(60, 250, 10);
                          case 'tad': return selectOptions(40, 150, 5);
                          case 'temp': return selectOptions(35.0, 41.0, 0.1);
                          case 'spo2': return selectOptions(70, 100, 1).reverse();
                          case 'glucosa': return selectOptions(40, 500, 10);
                          case 'dolor': return selectOptions(0, 10, 1);
                          default: return [];
                        }
                      };

                      return (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-0.5 py-0.5">
                            <input type="time" value={row.hora || ''} onChange={e => setSvRow(idx, 'hora', e.target.value)} disabled={readOnly} className="w-full border border-slate-200 rounded px-1 py-1 text-xs text-center focus:border-[#1a7a4a] focus:outline-none disabled:bg-slate-50" style={{ minWidth: '70px' }} />
                          </td>
                          {['fc', 'fr', 'tas', 'tad', 'temp', 'spo2', 'glucosa'].map(k => (
                            <td key={k} className="px-0.5 py-0.5">
                              <select value={row[k] || ''} onChange={e => setSvRow(idx, k, e.target.value)} disabled={readOnly} className="w-full border border-slate-200 rounded px-1 py-1 text-xs text-center bg-white focus:border-[#1a7a4a] focus:outline-none disabled:bg-slate-50 cursor-pointer appearance-none" style={{ minWidth: '50px' }}>
                                <option value="" disabled>-</option>
                                {getOptionsForKey(k).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                            </td>
                          ))}
                          {['ekg', 'pupilas', 'piel', 'llenado_cap'].map(k => (
                            <td key={k} className="px-0.5 py-0.5">
                              <input type="text" value={row[k] || ''} onChange={e => setSvRow(idx, k, e.target.value)} disabled={readOnly} className="w-full border border-slate-200 rounded px-1 py-1 text-xs text-center focus:border-[#1a7a4a] focus:outline-none disabled:bg-slate-50" style={{ minWidth: '55px' }} />
                            </td>
                          ))}
                          <td className="px-0.5 py-0.5">
                            <select value={row.dolor || ''} onChange={e => setSvRow(idx, 'dolor', e.target.value)} disabled={readOnly} className="w-full border border-slate-200 rounded px-1 py-1 text-xs text-center bg-white focus:border-[#1a7a4a] focus:outline-none disabled:bg-slate-50 cursor-pointer appearance-none" style={{ minWidth: '45px' }}>
                              <option value="" disabled>-</option>
                              {getOptionsForKey('dolor').map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          </td>
                          <td className="px-0.5 py-0.5 text-center">
                            {!readOnly && (form.sv_rows || []).length > 1 && (
                              <button type="button" onClick={() => removeSvRow(idx)} className="text-red-400 hover:text-red-600 text-xs font-bold" data-testid={`sv-remove-${idx}`}>X</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {!readOnly && (
                <button
                  type="button"
                  onClick={addSvRow}
                  className="hidden sm:flex items-center gap-1 text-xs text-[#1a7a4a] font-bold hover:text-[#145d38] transition-colors"
                  data-testid="sv-add-row-btn"
                >
                  + Agregar Toma de Signos Vitales
                </button>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-slate-100">
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Pupilas comunes</p>
                  <div className="flex flex-wrap gap-1">
                    {['Isocóricas', 'Anisocóricas', 'Midriasis', 'Miosis', 'Reactivas', 'No Reactivas'].map(v => (
                      <button key={v} type="button" disabled={readOnly}
                        className="text-[9px] px-1.5 py-0.5 border border-slate-300 rounded hover:bg-[#1a7a4a]/10 hover:border-[#1a7a4a] transition-colors disabled:opacity-50 hidden sm:inline-block"
                        onClick={() => {
                          const rows = [...(form.sv_rows || [])];
                          const last = rows.length - 1;
                          if (last >= 0) { rows[last] = { ...rows[last], pupilas: v }; setForm(p => ({ ...p, sv_rows: rows })); }
                        }}
                      >{v}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Piel común</p>
                  <div className="flex flex-wrap gap-1">
                    {['Normal', 'Pálida', 'Cianótica', 'Diaforética', 'Caliente', 'Fría', 'Seca', 'Húmeda'].map(v => (
                      <button key={v} type="button" disabled={readOnly}
                        className="text-[9px] px-1.5 py-0.5 border border-slate-300 rounded hover:bg-[#1a7a4a]/10 hover:border-[#1a7a4a] transition-colors disabled:opacity-50 hidden sm:inline-block"
                        onClick={() => {
                          const rows = [...(form.sv_rows || [])];
                          const last = rows.length - 1;
                          if (last >= 0) { rows[last] = { ...rows[last], piel: v }; setForm(p => ({ ...p, sv_rows: rows })); }
                        }}
                      >{v}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Llenado Capilar</p>
                  <div className="flex flex-wrap gap-1">
                    {['< 2 seg', '2-4 seg', '> 4 seg', 'Ausente'].map(v => (
                      <button key={v} type="button" disabled={readOnly}
                        className="text-[9px] px-1.5 py-0.5 border border-slate-300 rounded hover:bg-[#1a7a4a]/10 hover:border-[#1a7a4a] transition-colors disabled:opacity-50 hidden sm:inline-block"
                        onClick={() => {
                          const rows = [...(form.sv_rows || [])];
                          const last = rows.length - 1;
                          if (last >= 0) { rows[last] = { ...rows[last], llenado_cap: v }; setForm(p => ({ ...p, sv_rows: rows })); }
                        }}
                      >{v}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">EKG común</p>
                  <div className="flex flex-wrap gap-1">
                    {['Sinusal', 'Taquicardia', 'Bradicardia', 'Fibrilación', 'Asistolia'].map(v => (
                      <button key={v} type="button" disabled={readOnly}
                        className="text-[9px] px-1.5 py-0.5 border border-slate-300 rounded hover:bg-[#1a7a4a]/10 hover:border-[#1a7a4a] transition-colors disabled:opacity-50 hidden sm:inline-block"
                        onClick={() => {
                          const rows = [...(form.sv_rows || [])];
                          const last = rows.length - 1;
                          if (last >= 0) { rows[last] = { ...rows[last], ekg: v }; setForm(p => ({ ...p, sv_rows: rows })); }
                        }}
                      >{v}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── OBSERVACIONES + ESCALAS ── */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <SH id="escalas" title="Observaciones / Escalas" />
            <div className="p-4 space-y-4">
              <TA label="Observaciones" name="observaciones" value={form.observaciones} onChange={set} rows={3} />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2 border-t border-slate-100">
                {/* Glasgow Coma Scale - Detailed */}
                <div>
                  <p className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest mb-3">Escala de Coma de Glasgow</p>
                  <div className="space-y-3">
                    {/* Ocular */}
                    <div className="bg-slate-50 rounded-lg p-2.5">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-xs font-black text-[#1a3a5c] uppercase">Respuesta Ocular (E)</p>
                        {form.glasgow_ocular && <span className="bg-[#1a7a4a] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">{form.glasgow_ocular} pts</span>}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {[
                          { v: '4', label: 'Espontánea' },
                          { v: '3', label: 'A la voz' },
                          { v: '2', label: 'Al dolor' },
                          { v: '1', label: 'Ninguna' },
                        ].map(o => (
                          <button key={o.v} type="button" disabled={readOnly}
                            onClick={() => set('glasgow_ocular', o.v)}
                            className={`text-[10px] px-2 py-1 rounded border-2 font-bold transition-all ${form.glasgow_ocular === o.v ? 'bg-[#1a7a4a] border-[#1a7a4a] text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-[#1a7a4a]'} disabled:opacity-60`}
                            data-testid={`glasgow-ocular-${o.v}`}
                          >{o.v}: {o.label}</button>
                        ))}
                      </div>
                    </div>
                    {/* Verbal */}
                    <div className="bg-slate-50 rounded-lg p-2.5">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-xs font-black text-[#1a3a5c] uppercase">Respuesta Verbal (V)</p>
                        {form.glasgow_verbal && <span className="bg-[#1a7a4a] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">{form.glasgow_verbal} pts</span>}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {[
                          { v: '5', label: 'Orientada' },
                          { v: '4', label: 'Confusa' },
                          { v: '3', label: 'Inapropiada' },
                          { v: '2', label: 'Incomprensible' },
                          { v: '1', label: 'Ninguna' },
                        ].map(o => (
                          <button key={o.v} type="button" disabled={readOnly}
                            onClick={() => set('glasgow_verbal', o.v)}
                            className={`text-[10px] px-2 py-1 rounded border-2 font-bold transition-all ${form.glasgow_verbal === o.v ? 'bg-[#1a7a4a] border-[#1a7a4a] text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-[#1a7a4a]'} disabled:opacity-60`}
                            data-testid={`glasgow-verbal-${o.v}`}
                          >{o.v}: {o.label}</button>
                        ))}
                      </div>
                    </div>
                    {/* Motor */}
                    <div className="bg-slate-50 rounded-lg p-2.5">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-xs font-black text-[#1a3a5c] uppercase">Respuesta Motora (M)</p>
                        {form.glasgow_motor && <span className="bg-[#1a7a4a] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">{form.glasgow_motor} pts</span>}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {[
                          { v: '6', label: 'Obedece' },
                          { v: '5', label: 'Localiza' },
                          { v: '4', label: 'Retira' },
                          { v: '3', label: 'Flexión' },
                          { v: '2', label: 'Extensión' },
                          { v: '1', label: 'Ninguna' },
                        ].map(o => (
                          <button key={o.v} type="button" disabled={readOnly}
                            onClick={() => set('glasgow_motor', o.v)}
                            className={`text-[10px] px-2 py-1 rounded border-2 font-bold transition-all ${form.glasgow_motor === o.v ? 'bg-[#1a7a4a] border-[#1a7a4a] text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-[#1a7a4a]'} disabled:opacity-60`}
                            data-testid={`glasgow-motor-${o.v}`}
                          >{o.v}: {o.label}</button>
                        ))}
                      </div>
                    </div>
                    {/* Total */}
                    <div className="flex items-center gap-3 bg-[#1a3a5c] rounded-lg px-3 py-2">
                      <p className="text-white text-xs font-bold uppercase flex-1">Total Glasgow (E+V+M)</p>
                      <div className="bg-white rounded px-3 py-1 text-center min-w-[50px]">
                        <span className="text-2xl font-black text-[#1a3a5c]" data-testid="field-glasgow_total">{form.glasgow_total || '—'}</span>
                        <p className="text-[9px] text-slate-400">/ 15</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest mb-2">Escala Prehospitalaria de Cincinnati</p>
                  <div className="space-y-2">
                    {[
                      ['Asimetría Facial', 'cincinnati_asimetria_facial'],
                      ['Paresia de los Brazos', 'cincinnati_paresia_brazos'],
                      ['Alteración del Lenguaje', 'cincinnati_alteracion_lenguaje'],
                    ].map(([lbl, n]) => (
                      <div key={n} className="flex items-center gap-4">
                        <span className="text-xs text-slate-700 uppercase font-medium flex-1">{lbl}</span>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => set(n, 'SI')} className={`text-xs px-3 py-1 border-2 rounded-sm font-bold transition-all ${form[n] === 'SI' ? 'bg-[#1a7a4a] border-[#1a7a4a] text-white' : 'border-slate-300 text-slate-600 hover:border-[#1a7a4a]'}`} data-testid={`btn-${n}-si`}>Sí</button>
                          <button type="button" onClick={() => set(n, 'NO')} className={`text-xs px-3 py-1 border-2 rounded-sm font-bold transition-all ${form[n] === 'NO' ? 'bg-red-600 border-red-600 text-white' : 'border-slate-300 text-slate-600 hover:border-red-500'}`} data-testid={`btn-${n}-no`}>No</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Escala de Trauma — INTERACTIVA */}
              <div className="pt-2 border-t border-slate-100">
                <p className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest mb-3">Escala de Trauma — Toca el rango que corresponde al paciente</p>
                <div className="space-y-2" data-testid="trauma-scale-table">
                  {[
                    {
                      key: 'trauma_gcs', label: '(A) GCS', sub: 'Glasgow',
                      opts: [{ v: '14–15', s: 5 }, { v: '11–13', s: 4 }, { v: '8–10', s: 3 }, { v: '5–7', s: 2 }, { v: '3–4', s: 1 }, { v: '0', s: 0 }]
                    },
                    {
                      key: 'trauma_pas', label: '(B) PAS', sub: 'mmHg',
                      opts: [{ v: '>90', s: 5 }, { v: '70–89', s: 4 }, { v: '50–69', s: 3 }, { v: '<50', s: 2 }, { v: 'Sin pulso', s: 1 }, { v: '0', s: 0 }]
                    },
                    {
                      key: 'trauma_fr', label: '(C) FR', sub: 'resp/min',
                      opts: [{ v: '10–24', s: 5 }, { v: '25–35', s: 4 }, { v: '>35', s: 3 }, { v: '1–9', s: 2 }, { v: '<1', s: 1 }, { v: '0', s: 0 }]
                    },
                    {
                      key: 'trauma_esf', label: '(D) Esf. Resp.', sub: '',
                      opts: [{ v: 'Normal', s: 4 }, { v: 'Retardado', s: 2 }, { v: 'Retracción', s: 0 }]
                    },
                    {
                      key: 'trauma_llen', label: '(E) Llen. Cap.', sub: '',
                      opts: [{ v: 'Normal', s: 2 }, { v: 'Retardado', s: 1 }, { v: 'Ausente', s: 0 }]
                    },
                  ].map(({ key, label, sub, opts }) => {
                    const currentScore = form[key] !== '' ? parseInt(form[key]) : null;
                    const selectedOpt = opts.find(o => o.s === currentScore);
                    return (
                      <div key={key} className="flex items-start gap-2 bg-slate-50 rounded-lg p-2.5">
                        <div className="w-24 flex-shrink-0 pt-0.5">
                          <p className="text-xs font-black text-[#1a3a5c] uppercase">{label}</p>
                          {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
                          {selectedOpt != null && (
                            <div className="mt-1 bg-[#1a7a4a] text-white text-xs font-black px-1.5 py-0.5 rounded text-center w-fit">
                              {selectedOpt.s} pts
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 flex-1">
                          {opts.map(opt => {
                            const sel = currentScore === opt.s;
                            return (
                              <button
                                key={opt.v}
                                type="button"
                                onClick={() => set(key, String(opt.s))}
                                className={`text-xs px-2.5 py-1.5 rounded border-2 font-bold transition-all active:scale-95 ${sel
                                  ? 'bg-[#1a7a4a] border-[#1a7a4a] text-white shadow-sm'
                                  : 'bg-white border-slate-300 text-slate-700 hover:border-[#1a7a4a] hover:text-[#1a7a4a]'}`}
                                data-testid={`trauma-${key}-${opt.s}`}
                              >
                                {opt.v}
                              </button>
                            );
                          })}
                          {currentScore !== null && (
                            <button
                              type="button"
                              onClick={() => set(key, '')}
                              className="text-[10px] px-2 py-1 rounded border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-300 transition-all"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Auto-sum total */}
                <div className="mt-3 flex items-center gap-4 bg-[#1a3a5c] rounded-lg px-4 py-3">
                  <div className="flex-1">
                    <p className="text-white text-xs font-bold uppercase tracking-wider">Puntuación Total (A+B+C+D+E)</p>
                    <p className="text-slate-400 text-[10px]">Calculado automáticamente</p>
                  </div>
                  <div className="bg-white rounded-lg px-4 py-2 text-center min-w-[60px]">
                    <span className="text-3xl font-black text-[#1a3a5c]" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="field-trauma_total">
                      {form.trauma_total || '—'}
                    </span>
                    <p className="text-[10px] text-slate-400 leading-tight">pts</p>
                  </div>
                </div>
              </div>

              {/* Destino */}
              <div className="pt-2 border-t border-slate-100">
                <p className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest mb-2">Destino</p>
                <div className="flex gap-4">
                  <CB label="Trasladado" name="destino_trasladado" checked={form.destino_trasladado} onChange={set} />
                  <CB label="No Trasladado" name="destino_no_trasladado" checked={form.destino_no_trasladado} onChange={set} />
                  <CB label="Fuga" name="destino_fuga" checked={form.destino_fuga} onChange={set} />
                </div>
              </div>
            </div>
          </div>

          {/* ── DATOS MATERNOS / RECIÉN NACIDO ── */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <SH id="maternal" title="Datos de la Madre / Post-Parto / Recién Nacido" />
            <div className="p-4 space-y-4">
              <div>
                <p className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest mb-2">Datos de la Madre</p>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-3">
                  <F label="Gesta" name="gesta" value={form.gesta} onChange={set} />
                  <F label="Cesáreas" name="cesareas" value={form.cesareas} onChange={set} />
                  <F label="Para" name="para" value={form.para} onChange={set} />
                  <F label="Abortos" name="abortos" value={form.abortos} onChange={set} />
                  <F label="FUM" name="fum" value={form.fum} onChange={set} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <F label="Semanas de Gestación" name="semanas_gestacion" value={form.semanas_gestacion} onChange={set} type="number" />
                  <F label="Fecha Probable de Parto" name="fecha_probable_parto" value={form.fecha_probable_parto} onChange={set} type="date" />
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <F label="Hora Inicio Contracciones" name="hora_contracciones" value={form.hora_contracciones} onChange={set} type="time" />
                  <F label="Frecuencia" name="frec_contracciones" value={form.frec_contracciones} onChange={set} />
                  <F label="Duración" name="dur_contracciones" value={form.dur_contracciones} onChange={set} />
                </div>
              </div>
              <div className="pt-3 border-t border-slate-100">
                <p className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest mb-2">Datos Post-Parto</p>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Hora de Nacimiento" name="hora_nacimiento" value={form.hora_nacimiento} onChange={set} type="time" />
                  <F label="Lugar" name="lugar_nacimiento" value={form.lugar_nacimiento} onChange={set} />
                </div>
                <div className="mt-2">
                  <CB label="Placenta Expulsada" name="placenta_expulsada" checked={form.placenta_expulsada} onChange={set} />
                </div>
              </div>
              <div className="pt-3 border-t border-slate-100">
                <p className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest mb-2">Datos Recién Nacido</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                  <div>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Producto</p>
                    <div className="flex gap-3">
                      <CB label="Vivo" name="prod_vivo" checked={form.prod_vivo} onChange={set} />
                      <CB label="Muerto" name="prod_muerto" checked={form.prod_muerto} onChange={set} />
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Sexo</p>
                    <div className="flex gap-2">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" name="recien_sexo" value="MASC" checked={form.recien_sexo === 'MASC'} onChange={() => set('recien_sexo', 'MASC')} className="accent-[#1a7a4a]" />
                        <span className="text-xs uppercase font-medium text-slate-700">Masc</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" name="recien_sexo" value="FEM" checked={form.recien_sexo === 'FEM'} onChange={() => set('recien_sexo', 'FEM')} className="accent-[#1a7a4a]" />
                        <span className="text-xs uppercase font-medium text-slate-700">Fem</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* ESCALA DE APGAR */}
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <p className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-widest mb-3">Escala de APGAR</p>
                  <div className="space-y-2">
                    {[
                      { key: 'apgar_apariencia', label: '(A) Apariencia', opts: [{ v: 'Cianosis / Palidez', s: 0 }, { v: 'Acrocianosis', s: 1 }, { v: 'Rosado', s: 2 }] },
                      { key: 'apgar_pulso', label: '(P) Pulso / FC', opts: [{ v: 'Ausente', s: 0 }, { v: '< 100 lpm', s: 1 }, { v: '> 100 lpm', s: 2 }] },
                      { key: 'apgar_gesticulacion', label: '(G) Gesticulación', opts: [{ v: 'Sin respuesta', s: 0 }, { v: 'Muecas', s: 1 }, { v: 'Llanto / Tos', s: 2 }] },
                      { key: 'apgar_actividad', label: '(A) Actividad', opts: [{ v: 'Flacidez', s: 0 }, { v: 'Flexión extremid.', s: 1 }, { v: 'Mov. activos', s: 2 }] },
                      { key: 'apgar_respiracion', label: '(R) Respiración', opts: [{ v: 'Ausente', s: 0 }, { v: 'Lento/Irregular', s: 1 }, { v: 'Regular/Llanto', s: 2 }] },
                    ].map(({ key, label, opts }) => {
                      const currentScore = form[key] !== '' ? parseInt(form[key]) : null;
                      return (
                        <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-2 bg-slate-50 rounded-lg p-2.5">
                          <div className="w-32 flex-shrink-0">
                            <p className="text-xs font-black text-[#1a3a5c] uppercase">{label}</p>
                            {currentScore !== null && (
                              <div className="mt-1 bg-[#1a7a4a] text-white text-[10px] font-black px-1.5 py-0.5 rounded text-center w-fit">
                                {currentScore} pt{currentScore !== 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-wrap flex-1 gap-1.5">
                            {opts.map(opt => {
                              const sel = currentScore === opt.s;
                              return (
                                <button
                                  key={opt.s}
                                  type="button"
                                  onClick={() => set(key, String(opt.s))}
                                  className={`flex-1 min-w-[30%] text-[10px] leading-tight px-1.5 py-1.5 rounded border-2 font-bold transition-all active:scale-95 ${sel
                                    ? 'bg-[#1a7a4a] border-[#1a7a4a] text-white shadow-sm'
                                    : 'bg-white border-slate-300 text-slate-700 hover:border-[#1a7a4a] hover:text-[#1a7a4a]'}`}
                                >
                                  {opt.v}
                                </button>
                              );
                            })}
                            {currentScore !== null && (
                              <button type="button" onClick={() => set(key, '')} className="text-[10px] px-2 py-1.5 rounded border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-300 transition-all flex-shrink-0">
                                ✕
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* APGAR Total & Category */}
                  <div className="mt-3 flex items-center justify-between bg-[#1a3a5c] rounded-lg p-3">
                    <div>
                      <p className="text-white text-xs font-bold uppercase tracking-wider">Puntuación Total APGAR</p>
                      <div className="mt-1">
                        {form.apgar_total !== '' && form.apgar_total !== undefined && (() => {
                          const t = parseInt(form.apgar_total);
                          if (t >= 7) return <span className="inline-block bg-[#22c55e] text-[#064e3b] font-black uppercase text-[10px] px-2 py-0.5 rounded shadow-sm">Óptimo (7-10)</span>;
                          if (t >= 4) return <span className="inline-block bg-[#eab308] text-[#713f12] font-black uppercase text-[10px] px-2 py-0.5 rounded shadow-sm">Depresión moderada (4-6)</span>;
                          return <span className="inline-block bg-[#ef4444] text-white font-black uppercase text-[10px] px-2 py-0.5 rounded shadow-sm">Depresión severa (&lt; 3)</span>;
                        })()}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg px-4 py-2 text-center min-w-[60px] flex items-end justify-center gap-1">
                      <span className="text-3xl font-black text-[#1a3a5c] leading-none">{form.apgar_total || '—'}</span>
                      <span className="text-[10px] text-slate-400 font-bold mb-1">/ 10</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100">
                  <F label="Producto (descripción)" name="prod_texto" value={form.prod_texto} onChange={set} />
                </div>
              </div>
            </div>
          </div>

          {/* ── FIRMAS Y CONSENTIMIENTOS ── */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <SH id="firmas" title="Firmas y Consentimientos Legales" />
            <div className="p-4 space-y-6">
              {/* NEGATIVA */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h3 className="font-black text-[#1a3a5c] text-sm uppercase tracking-wider mb-2">Negativa a Recibir Atención / Ser Trasladado — Eximente de Responsabilidad</h3>
                <p className="text-xs text-slate-700 leading-relaxed bg-white border border-slate-200 p-3 rounded select-none font-medium">
                  DECLARO QUE NO ACEPTO LAS RECOMENDACIONES DEL PERSONAL DE LA AMBULANCIA DEL CUERPO DE RESCATE EN CUANTO AL (TRATAMIENTO) Y/O (TRASLADO) A UN HOSPITAL; POR LO QUE EXIMO AL CUERPO DE RESCATE Y A DICHAS PERSONAS DE TODA RESPONSABILIDAD QUE PUDIERA DERIVAR AL HABER RESPETADO Y CUMPLIDO MI DECISIÓN.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
                  <div className="space-y-2">
                    <SignatureCanvas label="Nombre/Firma del Paciente" onSave={d => set('firma_pac_negativa', d)} width={280} height={90} initialValue={form.firma_pac_negativa} />
                    <F label="Nombre del Paciente" name="nombre_pac_negativa" value={form.nombre_pac_negativa} onChange={set} />
                  </div>
                  <div className="space-y-2">
                    <SignatureCanvas label="Nombre/Firma del Testigo" onSave={d => set('firma_testigo', d)} width={280} height={90} initialValue={form.firma_testigo} />
                    <F label="Nombre del Testigo" name="nombre_testigo" value={form.nombre_testigo} onChange={set} />
                  </div>
                </div>
              </div>

              {/* CONSENTIMIENTO */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-black text-[#1a3a5c] text-sm uppercase tracking-wider mb-2">Consentimiento Informado</h3>
                <p className="text-xs text-slate-700 leading-relaxed bg-white border border-slate-200 p-3 rounded select-none font-medium">
                  PREVIO A UNA DETALLADA EXPLICACIÓN DOY MI CONSENTIMIENTO A SER TRASLADADO Y/O ATENDIDO POR EL PERSONAL DEL CUERPO DE RESCATE A.C.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
                  <div className="space-y-2">
                    <SignatureCanvas label="Nombre/Firma del Paciente" onSave={d => set('firma_pac_consentimiento', d)} width={280} height={90} initialValue={form.firma_pac_consentimiento} />
                    <F label="Nombre del Paciente" name="nombre_pac_consentimiento" value={form.nombre_pac_consentimiento} onChange={set} />
                  </div>
                  <div className="space-y-2">
                    <SignatureCanvas label="Nombre/Firma de Familiar o Tutor" onSave={d => set('firma_familiar', d)} width={280} height={90} initialValue={form.firma_familiar} />
                    <F label="Nombre del Familiar o Tutor" name="nombre_familiar" value={form.nombre_familiar} onChange={set} />
                  </div>
                </div>
              </div>

              {/* AUTORIDADES */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-black text-[#1a3a5c] text-sm uppercase tracking-wider mb-4">Autoridades que Intervinieron</h3>
                <div className="mb-6">
                  <TA label="Otras Autoridades / Información Adicional" name="autoridades_extra" value={form.autoridades_extra} onChange={set} rows={2} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <SignatureCanvas label="Entrega Paciente" onSave={d => set('firma_entrega', d)} width={280} height={90} initialValue={form.firma_entrega} />
                    <F label="Nombre y Firma — Entrega Paciente" name="nombre_entrega" value={form.nombre_entrega} onChange={set} />
                  </div>
                  <div className="space-y-2">
                    <SignatureCanvas label="Médico que Recibe" onSave={d => set('firma_medico', d)} width={280} height={90} initialValue={form.firma_medico} />
                    <F label="Nombre y Firma — Médico que Recibe" name="nombre_medico" value={form.nombre_medico} onChange={set} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom action */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pb-8">
            {!readOnly && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center justify-center gap-2 bg-white border-2 border-[#1a7a4a] hover:bg-[#1a7a4a]/10 disabled:opacity-60 text-[#1a7a4a] font-black text-sm px-8 py-3.5 rounded-md transition-all shadow-sm active:scale-95 uppercase tracking-wide"
                data-testid="save-frap-bottom-btn"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                {saving ? 'Guardando...' : (isEdit ? 'Actualizar FRAP' : 'Guardar FRAP')}
              </button>
            )}
            <button
              onClick={handleGeneratePdf}
              disabled={generating}
              className="flex items-center justify-center gap-2 bg-[#1a7a4a] hover:bg-[#145d38] disabled:opacity-60 text-white font-black text-sm px-8 py-3.5 rounded-md transition-all shadow-md active:scale-95 uppercase tracking-wide"
              data-testid="generate-pdf-bottom-btn"
            >
              {generating ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileDown className="w-5 h-5" />}
              {generating ? 'Generando PDF...' : 'Generar PDF Oficial'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
